import React, { useState } from 'react';
import classNames from 'classnames';
import { ARRAY_ERROR } from 'final-form';
import { Form as FinalForm, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import isEqual from 'lodash/isEqual';

// Import configs and util modules
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { nonEmptyArray, composeValidators } from '../../util/validators';
import { isUploadImageOverLimitError } from '../../util/errors';
import { downloadImagesAsZip, downloadImage } from '../../util/imagesHelpers';

// Import shared components
import {
  Button,
  Form,
  AspectRatioWrapper,
  FieldCheckbox,
  IconDownload
} from '../../components';

// Import modules from this directory
import ListingImage from './ListingImage';
import css from './EditListingCard.module.css';

const ACCEPT_IMAGES = 'image/*';

const ImageUploadError = props => {
  return props.uploadOverLimit ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.imageUploadFailed.uploadOverLimit" />
    </p>
  ) : props.uploadImageError ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.imageUploadFailed.uploadFailed" />
    </p>
  ) : null;
};

// Field component that uses file-input to allow user to select images.
const FieldAddImage = props => {
  const { formApi, onImageUploadHandler, aspectWidth = 1, aspectHeight = 1, ...rest } = props;
  return (
    <Field form={null} {...rest}>
      {fieldprops => {
        const { accept, input, label, disabled: fieldDisabled } = fieldprops;
        const { name, type } = input;
        const onChange = e => {
          const files = Array.from(e.target.files);
          formApi.change(`addImage`, files);
          formApi.blur(`addImage`);
          files.forEach(file => onImageUploadHandler(file));
        };
        const inputProps = { accept, id: name, name, onChange, type, multiple: true };
        return (
          <div className={css.addImageWrapper}>
            <AspectRatioWrapper width={aspectWidth} height={aspectHeight}>
              {fieldDisabled ? null : <input {...inputProps} className={css.addImageInput} />}
              <label htmlFor={name} className={css.addImage}>
                {label}
              </label>
            </AspectRatioWrapper>
          </div>
        );
      }}
    </Field>
  );
};

// Component that shows listing images from "images" field array
const FieldListingImage = props => {
  const {
    name,
    intl,
    onRemoveImage,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    imageName,
    onDragStart,
    onDrop,
    index
  } = props;

  return (
    <Field name={name}>
      {fieldProps => {
        const { input } = fieldProps;
        const image = input.value;

        const url = image?.attributes?.variants?.["scaled-xlarge"]?.url
          || image?.attributes?.variants?.["default"]?.url
          || image?.attributes?.variants?.["listing-card"]?.url
          || image?.attributes?.variants?.["listing-card-2x"]?.url;

        return image ? (
          <div
            draggable
            onDragStart={() => onDragStart(index)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(index)}
            className={css.thumbnailHolder}
          >
            <ListingImage
              image={image}
              key={image?.id?.uuid || image?.id}
              className={css.thumbnail}
              savedImageAltText={intl.formatMessage({
                id: 'EditListingPhotosForm.savedImageAltText',
              })}
              onRemoveImage={() => onRemoveImage(image?.id)}
              aspectWidth={aspectWidth}
              aspectHeight={aspectHeight}
              variantPrefix={variantPrefix}
              imageName={imageName}
            />

            <div className={css.thumbnailButtons}>
              <div
                className={classNames(css.thumbnailButton, css.thumbnailButtonDownload)}
                onClick={() => downloadImage(url, imageName)}
              >
                <IconDownload />
                <FormattedMessage id="EditListingsPage.download" />
              </div>

              <FieldCheckbox
                id={image?.id?.uuid || image?.id}
                name="selectedImages"
                label={intl.formatMessage({
                  id: 'EditListingsPage.select',
                })}
                value={image}
                className={classNames(css.thumbnailButton, css.thumbnailButtonSelect)}
              />
            </div>
          </div>
        ) : null;
      }}
    </Field>
  );
};

/**
 * The EditListingCardForm component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {boolean} props.ready - Whether the form is ready
 * @param {boolean} props.updated - Whether the form is updated
 * @param {boolean} props.updateInProgress - Whether the update is in progress
 * @param {Object} props.fetchErrors - The fetch errors object
 * @param {propTypes.error} props.fetchErrors.publishListingError - The publish listing error
 * @param {propTypes.error} props.fetchErrors.showListingsError - The show listings error
 * @param {propTypes.error} props.fetchErrors.uploadImageError - The upload image error
 * @param {propTypes.error} props.fetchErrors.updateListingError - The update listing error
 * @param {string} props.saveActionMsg - The save action message
 * @param {Function} props.onSubmit - The submit function
 * @param {Function} props.onImageUpload - The image upload function
 * @param {Function} props.onRemoveImage - The remove image function
 * @param {Object} props.listingImageConfig - The listing image config
 * @param {number} props.listingImageConfig.aspectWidth - The aspect width
 * @param {number} props.listingImageConfig.aspectHeight - The aspect height
 * @param {string} props.listingImageConfig.variantPrefix - The variant prefix
 * @returns {JSX.Element}
 */
export const EditListingCardForm = props => {
  const [state, setState] = useState({ imageUploadRequested: false });
  const [submittedImages, setSubmittedImages] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);

  const onImageUploadHandler = file => {
    const { listingImageConfig, onImageUpload, listingId } = props;

    setState(prev => ({ ...prev, imageUploadRequested: true }));

    onImageUpload({
      id: `${file.name}_${Date.now()}`,
      file
    }, listingImageConfig, listingId)
      .then(() => {
        setState(prev => ({ ...prev, imageUploadRequested: false }));
      })
      .catch(() => {
        setState(prev => ({ ...prev, imageUploadRequested: false }));
      });
  };
  const intl = useIntl();

  const getImagesUrls = (images) =>
    images.map(img =>
      img?.attributes?.variants?.["scaled-xlarge"]?.url ||
      img?.attributes?.variants?.["default"]?.url
    );

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          form,
          className,
          fetchErrors,
          handleSubmit,
          invalid,
          onRemoveImage,
          disabled,
          ready,
          updated,
          updateInProgress,
          touched,
          errors,
          values,
          listingImageConfig,
          slug,
          currentUserId,
          listingId,
          onUpdateListing,
        } = formRenderProps;

        const images = values.images || [];
        const imagesUrls = getImagesUrls(images);
        const selectedImages = values?.selectedImages || [];
        const selectedImagesUrls = getImagesUrls(selectedImages);
        const { aspectWidth = 1, aspectHeight = 1, variantPrefix } = listingImageConfig;

        const { updateListingError, uploadImageError } =
          fetchErrors || {};
        const uploadOverLimit = isUploadImageOverLimitError(uploadImageError);

        // imgs can contain added images (with temp ids) and submitted images with uniq ids.
        const arrayOfImgIds = imgs => imgs?.map(i => (typeof i.id === 'string' ? i.imageId : i.id));
        const imageIdsFromProps = arrayOfImgIds(images);
        const imageIdsFromPreviousSubmit = arrayOfImgIds(submittedImages);
        const imageArrayHasSameImages = isEqual(imageIdsFromProps, imageIdsFromPreviousSubmit);
        const submittedOnce = submittedImages.length > 0;
        const pristineSinceLastSubmit = submittedOnce && imageArrayHasSameImages;

        const submitReady = ready;
        const submitInProgress = updateInProgress;
        const submitDisabled =
          invalid || disabled || submitInProgress || state.imageUploadRequested || ready;
        const imagesError = touched.images && errors?.images && errors.images[ARRAY_ERROR];

        const onDragStart = (index) => {
          setDragIndex(index);
        };

        const onDrop = (index) => {
          const newImages = Array.from(values.images);
          const [movedImage] = newImages.splice(dragIndex, 1);
          newImages.splice(index, 0, movedImage);
          form.change('images', newImages);

          onUpdateListing(listingId, newImages, currentUserId);
        };

        return (
          <Form
            className={css.rootForm}
            onSubmit={e => {
              setSubmittedImages(images);
              handleSubmit(e);
            }}
          >
            {updateListingError ? (
              <p className={css.error}>
                <FormattedMessage id="EditListingPhotosForm.updateFailed" />
              </p>
            ) : null}

            <div className={css.imagesFieldArray}>
              <FieldArray
                name="images"
                validate={composeValidators(
                  nonEmptyArray(
                    intl.formatMessage({
                      id: 'EditListingPhotosForm.imageRequired',
                    })
                  )
                )}
              >
                {({ fields }) =>
                  fields.map((name, index) => (
                    <FieldListingImage
                      key={name}
                      name={name}
                      onRemoveImage={imageId => {
                        fields.remove(index);
                        onRemoveImage(imageId);
                      }}
                      intl={intl}
                      aspectWidth={aspectWidth}
                      aspectHeight={aspectHeight}
                      variantPrefix={variantPrefix}
                      imageName={`${slug}-${index + 1}.jpg`}
                      index={index}
                      onDragStart={onDragStart}
                      onDrop={onDrop}
                    />
                  ))
                }
              </FieldArray>

              <FieldAddImage
                id="addImage"
                name="addImage"
                accept={ACCEPT_IMAGES}
                label={
                  <span className={css.chooseImageText}>
                    <span className={css.chooseImage}>
                      <FormattedMessage id="EditListingPhotosForm.chooseImage" />
                    </span>
                    <span className={css.imageTypes}>
                      <FormattedMessage id="EditListingPhotosForm.imageTypes" />
                    </span>
                  </span>
                }
                type="file"
                disabled={state.imageUploadRequested}
                formApi={form}
                onImageUploadHandler={onImageUploadHandler}
                aspectWidth={aspectWidth}
                aspectHeight={aspectHeight}
              />
            </div>

            {imagesError ? <div className={css.arrayError}>{imagesError}</div> : null}

            <ImageUploadError
              uploadOverLimit={uploadOverLimit}
              uploadImageError={uploadImageError}
            />

            <div className={css.formButtons}>
              <Button
                className={css.downloadButton}
                type="button"
                onClick={() => downloadImagesAsZip(imagesUrls, slug)}
              >
                <FormattedMessage id="EditListingsPage.downloadAll" />
              </Button>
              <Button
                className={css.downloadButton}
                type="button"
                onClick={() => downloadImagesAsZip(selectedImagesUrls, slug)}
                disabled={!selectedImages?.length}
              >
                <FormattedMessage id="EditListingsPage.downloadSelected" />
              </Button>
              <Button
                className={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={submitReady}
              >
                <FormattedMessage id="EditListingsPage.save" />
              </Button>
            </div>

          </Form>
        );
      }}
    />
  );
};

export default EditListingCardForm;
